import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

// Portal User Types (matching backend enum)
export enum PortalUserType {
  Admin = 1,
  TeamMember = 2,
  Customer = 3
}

export interface CustomerSessionState {
  isAuthenticated: boolean;
  customerId: string | null;
  customerName: string | null;
  email: string | null;
  loading: boolean;
  error: string | null;
  hydrated: boolean; // indicates localStorage hydration completed
  // Portal User Type (1=Admin, 2=TeamMember, 3=Customer)
  portalUserType: PortalUserType | null;
  // System Profile name from TB_PROFILE (e.g., "Administrator", "Finance", etc.)
  profileName: string | null;
  // Emulation fields
  isEmulating: boolean;
  emulatedCustomerId: string | null;
  emulatedCustomerName: string | null;
}

// Decode JWT to get claims
function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const STORAGE_KEY = "sc_portal_session";

function loadFromStorage(): CustomerSessionState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CustomerSessionState;
  } catch {
    return null;
  }
}

function persist(state: CustomerSessionState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

const initialState: CustomerSessionState = loadFromStorage() || {
  isAuthenticated: false,
  customerId: null,
  customerName: null,
  email: null,
  loading: false,
  error: null,
  hydrated: false,
  portalUserType: null,
  profileName: null,
  isEmulating: false,
  emulatedCustomerId: null,
  emulatedCustomerName: null,
};

export const mockLogin = createAsyncThunk<
  { customerId: string; customerName: string; email: string; portalUserType: PortalUserType | null; profileName: string | null },
  { email: string; password: string },
  { rejectValue: string }
>(
  "customerSession/mockLogin",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://api-customer.spacecoastsrcmgt.com";
      const loginUrl = `${apiUrl}/api/Auth/Login`;

      console.log("[Login] Attempting login...");
      console.log("[Login] URL:", loginUrl);

      // Chama API real de login
      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.sucesso) {
        console.error("[Login] Failed:", data.mensagens);
        return rejectWithValue(data.mensagens?.[0] || "Invalid credentials");
      }

      // Store token in localStorage
      let portalUserType: PortalUserType | null = null;
      let profileName: string | null = null;
      let customerId = data.data?.customerId || "";

      if (data.data?.accessToken) {
        localStorage.setItem("authToken", data.data.accessToken);
        console.log("[Login] Token saved to localStorage");

        // Decode JWT to extract claims
        const claims = decodeJWT(data.data.accessToken);
        if (claims) {
          // Extract portal_user_type (determined from SystemProfile)
          const userTypeValue = claims["portal_user_type"];
          if (userTypeValue) {
            portalUserType = parseInt(String(userTypeValue), 10) as PortalUserType;
            console.log("[Login] Portal User Type:", portalUserType);
          }

          // Extract profile_name from TB_PROFILE
          const profileNameClaim = claims["profile_name"];
          if (profileNameClaim) {
            profileName = String(profileNameClaim);
            console.log("[Login] Profile Name:", profileName);
          }

          // Extract customer_id for Customer users
          const customerIdClaim = claims["customer_id"];
          if (customerIdClaim) {
            customerId = String(customerIdClaim);
            console.log("[Login] Customer ID from token:", customerId);
          }
        }
      }

      // Return user data
      return {
        customerId: customerId || "system-user",
        customerName: data.data?.email || email,
        email: email.trim().toLowerCase(),
        portalUserType,
        profileName,
      };
    } catch (error) {
      console.error("[Login Error]", error);
      return rejectWithValue("Failed to connect to server");
    }
  }
);

const slice = createSlice({
  name: "customerSession",
  initialState,
  reducers: {
    initialize(state: CustomerSessionState) {
      const stored = loadFromStorage();
      if (stored) {
        state.isAuthenticated = stored.isAuthenticated;
        state.customerId = stored.customerId;
        state.customerName = stored.customerName;
        state.email = stored.email;
        state.portalUserType = stored.portalUserType || null;
        state.profileName = stored.profileName || null;
        state.isEmulating = stored.isEmulating || false;
        state.emulatedCustomerId = stored.emulatedCustomerId || null;
        state.emulatedCustomerName = stored.emulatedCustomerName || null;
      }
      state.hydrated = true;
    },
    logout(state: CustomerSessionState) {
      state.isAuthenticated = false;
      state.customerId = null;
      state.customerName = null;
      state.email = null;
      state.error = null;
      state.hydrated = true;
      state.portalUserType = null;
      state.profileName = null;
      state.isEmulating = false;
      state.emulatedCustomerId = null;
      state.emulatedCustomerName = null;
      localStorage.removeItem("authToken"); // Also remove the auth token
      persist(state);
    },
    // Emulation actions
    startEmulation(
      state: CustomerSessionState,
      action: PayloadAction<{ customerId: string; customerName: string }>
    ) {
      state.isEmulating = true;
      state.emulatedCustomerId = action.payload.customerId;
      state.emulatedCustomerName = action.payload.customerName;
      persist(state);
      console.log("[Emulation] Starting emulation:", action.payload.customerName);
    },
    stopEmulation(state: CustomerSessionState) {
      state.isEmulating = false;
      state.emulatedCustomerId = null;
      state.emulatedCustomerName = null;
      persist(state);
      console.log("[Emulation] Emulation ended");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(mockLogin.pending, (state: CustomerSessionState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        mockLogin.fulfilled,
        (
          state: CustomerSessionState,
          action: PayloadAction<{
            customerId: string;
            customerName: string;
            email: string;
            portalUserType: PortalUserType | null;
            profileName: string | null;
          }>
        ) => {
          state.loading = false;
          state.isAuthenticated = true;
          state.customerId = action.payload.customerId;
          state.customerName = action.payload.customerName;
          state.email = action.payload.email;
          state.portalUserType = action.payload.portalUserType;
          state.profileName = action.payload.profileName;
          state.hydrated = true;
          persist(state);
        }
      )
      .addCase(mockLogin.rejected, (state: CustomerSessionState, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Authentication error";
        state.hydrated = true;
      });
  },
});

export const { initialize, logout, startEmulation, stopEmulation } =
  slice.actions;
export default slice.reducer;

// Selectors
export const selectCustomerSession = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession;
export const selectIsAuthenticated = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.isAuthenticated;
export const selectCustomerId = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.customerId;
export const selectIsEmulating = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.isEmulating;
export const selectEmulatedCustomerId = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.emulatedCustomerId;
export const selectEmulatedCustomerName = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.emulatedCustomerName;
export const selectEffectiveCustomerId = (s: {
  customerSession: CustomerSessionState;
}) =>
  s.customerSession.isEmulating
    ? s.customerSession.emulatedCustomerId
    : s.customerSession.customerId;
// Portal User Type Selectors
export const selectPortalUserType = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType;

export const selectIsPortalAdmin = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.Admin;

export const selectIsPortalTeamMember = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.TeamMember;

export const selectIsPortalCustomer = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.Customer;

// Can view all customers (Admin or TeamMember)
export const selectCanViewAllCustomers = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.Admin ||
  s.customerSession.portalUserType === PortalUserType.TeamMember;

// Can emulate customers (Admin or TeamMember)
export const selectCanEmulate = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.Admin ||
  s.customerSession.portalUserType === PortalUserType.TeamMember;

// Legacy selector - checks portal user type (Admin = 1)
// Note: portalUserType comes from JWT 'portal_user_type' claim
export const selectIsAdmin = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.portalUserType === PortalUserType.Admin ||
  s.customerSession.portalUserType === PortalUserType.TeamMember;

export const selectCustomerName = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.customerName;
export const selectSessionHydrated = (s: {
  customerSession: CustomerSessionState;
}) => s.customerSession.hydrated;

// Get profile name from TB_PROFILE
export const selectProfileName = (s: { customerSession: CustomerSessionState }) =>
  s.customerSession.profileName;

// Get portal user type label (uses profile name if available)
export const selectPortalUserTypeLabel = (s: { customerSession: CustomerSessionState }) => {
  // If we have a profile name from TB_PROFILE, use it
  if (s.customerSession.profileName) {
    return s.customerSession.profileName;
  }

  // Fallback to user type labels
  switch (s.customerSession.portalUserType) {
    case PortalUserType.Admin:
      return "Administrator";
    case PortalUserType.TeamMember:
      return "Team Member";
    case PortalUserType.Customer:
      return "Customer";
    default:
      return "Unknown";
  }
};
